/* Mossante cart. The ONLY cart code on the site (2026-09-23, audit C1-C6).

   Loaded on every template, before the page script (layout/theme.liquid), and
   exposed as window.MossanteCart = {open, close, refresh, add, change}.

   What it guarantees, and why each rule exists:
   - The drawer lists EVERY line in /cart.js. The page's own product comes
     first, in the picker's fixed order, zero rows included, so nothing jumps.
     Every other product is appended below "Also in your cart", never
     reordered, and never removed while the drawer is open (a row must not
     vanish under the finger). Before this, a Star page showed "Silver 0" over
     a footer that was billing 2 Marquise and 1 Heart (tester #13).
   - Split lines of one variant are summed into one row. The automatic
     discounts split a variant over several lines (3x Pink = paid 2 + free 1).
   - Every write goes through ONE serialized queue - the stepper taps AND the
     picker's Add button. Quantities are the shopper's per variant, but they
     are WRITTEN BY LINE KEY: the new total on the variant's first line, its
     other lines to 0, one fresh /cart.js read and one retry if a key is stale.
     Writing by variant id touched one line only: 3 -> 4 gave 5 (2026-09-22).
   - Taps paint at once (optimistic); the server gets the brush back only when
     the queue drains. The badge counts ALL rows. The page's own discount is
     recomputed by its known rule; another product's discount is unknown until
     the server answers, so the total is dimmed until then.
   - A tap is a DELTA (+1 / -1), never an absolute number read off the screen.
     The target is worked out inside the job, from the newest server cart: the
     previous job's answer, or a fresh GET /cart.js at the start of each burst
     of taps. The rows on screen can be stale - an Add still in flight, a page
     back from the back/forward cache, another tab - and an absolute quantity
     read from a stale row undid a 3-pack Add (review R1, R4, 2026-09-23).
   - The picker's Add shows in the rows at once, dimmed until the server
     answers, so nobody taps + on a row that still reads 0 during the Add.
   - Checkout waits for queued writes: navigating away kills every write not
     yet sent, and the shopper would check out a smaller cart than the one on
     screen (R2). A write that fails leaves the shopper here, with the server's
     cart on screen and the reason under the total (R5).
   - A retry after a LOST answer (network, 5xx) first checks whether the write
     landed, so a delta is never applied twice. A refused one (4xx, e.g. a
     stale line key) is recomputed on the fresh cart. The Add does the same
     check before it reports "could not add", so a retry never adds twice.
   - Every footer number comes from the server lines it describes: subtotal,
     one discount row per product (from line_level_discount_allocations), one
     per cart-level discount application, and the total. The progress line is
     about the page's own product only and names it when anything else is in
     the cart.
   ES5 on purpose, like the other Mossante scripts: an older phone must not be
   left with a cart that throws. */
(function () {
  'use strict';
  if (window.MossanteCart && window.MossanteCart._shared) return;

  /* Prices print in the shopper's currency (2026-09-24). Liquid and the cart
     already give amounts in the presentment currency, converted and rounded by
     Markets, which are the numbers checkout charges; this only picks the symbol.
     USD keeps the house style ($55, $69.50). Every page script and the cart
     carry the same two functions, so keep them identical. */
  var CUR = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  /* DP: the currency's own decimals (0 for JPY, KRW; 2 for EUR, AUD). Shopify's
     amounts are always in hundredths, so a zero-decimal currency is rounded to a
     whole unit here, or "3 for 2" would print ¥5,933.33 each (review 2026-09-24). */
  var DP = 2;
  try { DP = new Intl.NumberFormat('en', { style: 'currency', currency: CUR }).resolvedOptions().maximumFractionDigits; } catch (e) {}
  if (DP > 2) DP = 2;
  var money = function (cents) {
    var v = cents / 100;
    if (CUR === 'USD') return '$' + (v % 1 ? v.toFixed(2) : v);
    if (DP === 0) v = Math.round(v);
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: CUR,
        minimumFractionDigits: v % 1 ? DP : 0, maximumFractionDigits: DP }).format(v);
    } catch (e) { return CUR + ' ' + (v % 1 ? v.toFixed(DP) : v); }
  };

  /* data-offers holds the offer prices as typed in the theme editor, in the shop
     currency (USD), plus "base": the product's price as Liquid rendered it for
     this shopper, already converted and rounded by Markets. In any other
     currency the full price becomes base, and a lower offer price (the pair
     price) keeps its USD discount converted at the storefront rate, which is how
     Shopify converts a fixed-amount discount at checkout. In USD nothing moves. */
  function localOffers(offers) {
    if (CUR === 'USD' || !offers || !offers.length) return offers;
    var rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 0;
    var top = 0;
    offers.forEach(function (o) { if (o.unit > top) top = o.unit; });
    return offers.map(function (o) {
      var c = {}, k;
      for (k in o) if (Object.prototype.hasOwnProperty.call(o, k)) c[k] = o[k];
      /* Shopify rounds a converted fixed discount to the currency's smallest unit
         (a whole yen is 100 here), so round the same way or JPY pairs miss by <1 yen. */
      var step = Math.pow(10, 2 - DP);
      if (o.base) c.unit = o.base - Math.round((top - o.unit) * rate / step) * step;
      else if (rate) c.unit = Math.round(o.unit * rate / step) * step;
      return c;
    });
  }

  /* An HTTP error carries its status as .http, so a retry can tell "the server
     refused it" (4xx: nothing changed) from "the answer was lost" (a network
     error has no .http; a 5xx may have been applied). */
  var req = function (url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (r) {
      if (!r.ok) {
        return r.json().then(function (e) {
          if (!e || typeof e !== 'object') e = { description: '' };
          e.http = r.status;
          throw e;
        }, function () { throw { http: r.status }; });
      }
      return r.json();
    });
  };

  function noop() {}
  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (ch) {
      return ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;';
    });
  }
  /* "Mossante Marquise Bracelet" -> "Marquise Bracelet" */
  function shortTitle(t) {
    var s = String(t || '').replace(/^\s*the\s+/i, '').replace(/^\s*mossante\s+/i, '').replace(/^\s+|\s+$/g, '');
    return s || String(t || '');
  }
  /* A discount title follows a middle dot, so sentence case reads better in
     lower case: "Pair price" -> "pair price". An acronym ("BOGO") is left. */
  function lc1(t) {
    t = String(t || '');
    return t.length > 1 && t.charAt(1) === t.charAt(1).toLowerCase() ? t.charAt(0).toLowerCase() + t.slice(1) : t;
  }
  function thumb(url) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=240&height=240&crop=center';
  }

  var GIFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="3" y="8" width="18" height="4"/><path d="M5 12v8h14v-8"/>' +
    '<path d="M12 8v12M12 8c-2.5 0-4-1.2-4-3a2 2 0 0 1 4 0M12 8c2.5 0 4-1.2 4-3a2 2 0 0 0-4 0"/></svg>';

  /* ---------- the page: which product owns the fixed rows ---------- */
  /* From the picker section, absent on policy pages and /pages/contact.
     rule comes from data-offers only - no new setting, no invented price:
       b2g1 : an offer gives units free (the cord, Buy 2 get 1)
       pair : an offer is "pay 2+, free 0" at a lower unit (the pair price)
       none : a single price and nothing else */
  function readPage() {
    var pk = document.querySelector('[data-picker][data-colours]');
    if (!pk) return null;
    var colours = [];
    try { colours = JSON.parse(pk.getAttribute('data-colours')) || []; } catch (e) { colours = []; }
    if (!colours.length) return null;
    var ord = (pk.getAttribute('data-order') || '').split(',')
      .map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
    colours.sort(function (x, y) {
      var ix = ord.indexOf(String(x.id).toLowerCase()), iy = ord.indexOf(String(y.id).toLowerCase());
      return (ix === -1 ? 99 : ix) - (iy === -1 ? 99 : iy);
    });
    var offers = [];
    try { offers = localOffers(JSON.parse(pk.getAttribute('data-offers') || '[]') || []); } catch (e) { offers = []; }
    var one = offers.filter(function (o) { return o.pay === 1 && !o.free; })[0] || offers[0] || null;
    /* No fallback price: the old floral fallback was 14900, the retired $149
       (audit X8). 0 means "wait for the server" and says so. */
    var unit = one && one.unit ? one.unit : 0;
    if (!unit) console.warn('[mossante] cart: no unit price in data-offers; optimistic prices wait for the server');
    var fr = offers.filter(function (o) { return o.free > 0; })[0];
    var pr = offers.filter(function (o) { return o.pay >= 2 && !o.free; })[0];
    var rule = fr ? { type: 'b2g1', unit: fr.unit || unit, cycle: fr.pay + fr.free, freeN: fr.free }
             : pr && pr.unit ? { type: 'pair', unit: unit, pairUnit: pr.unit }
             : { type: 'none', unit: unit };
    var vids = {};
    colours.forEach(function (c) { vids[String(c.variant)] = 1; });
    return {
      pid: String(pk.getAttribute('data-product-id') || ''),
      title: pk.getAttribute('data-product-title') || '',
      noun: pk.getAttribute('data-noun') || 'bracelet',
      /* what one option is called (the picker's "word" setting); the cord says colour */
      word: (pk.getAttribute('data-word') || '').replace(/^\s+|\s+$/g, '') || 'colour',
      colours: colours, vids: vids,
      rdate: (pk.getAttribute('data-restock-date') || '').trim(),
      rule: rule
    };
  }

  /* ---------- the server cart, grouped ---------- */
  /* byV: one entry per variant, split lines SUMMED. byP: per product, the
     quantity, the discount the server allocated to its lines, how many of its
     units are free, and the discount titles. seq: variants in cart order. */
  function group(c) {
    var G = { byV: {}, byP: {}, seq: [] };
    each(c && c.items, function (it) {
      var vid = String(it.variant_id || it.id), pid = String(it.product_id || '');
      if (P && !P.pid && P.vids[vid]) P.pid = pid;
      var v = G.byV[vid];
      if (!v) {
        v = G.byV[vid] = {
          vid: vid, pid: pid, title: it.product_title, vtitle: it.variant_title, url: it.url,
          img: it.image || (it.featured_image && it.featured_image.url) || '',
          unit: it.original_price != null ? it.original_price : (it.price || 0),
          qty: 0, orig: 0, lines: []
        };
        G.seq.push(vid);
      }
      v.qty += it.quantity;
      v.orig += it.original_line_price != null ? it.original_line_price : (it.line_price || 0);
      v.lines.push({ key: it.key, qty: it.quantity });
      var p = G.byP[pid] || (G.byP[pid] = { pid: pid, title: it.product_title, qty: 0, disc: 0, free: 0, titles: [] });
      p.qty += it.quantity;
      var ld = it.line_level_total_discount;
      if (ld == null) { ld = 0; each(it.line_level_discount_allocations, function (a) { ld += a.amount || 0; }); }
      p.disc += ld;
      if (it.final_line_price === 0 && (it.original_line_price || 0) > 0) p.free += it.quantity;
      each(it.line_level_discount_allocations, function (a) {
        var t = a.discount_application && a.discount_application.title;
        if (t && p.titles.indexOf(t) < 0) p.titles.push(t);
      });
    });
    return G;
  }

  function othersIn(G) {
    for (var k in G.byP) if (G.byP.hasOwnProperty(k) && G.byP[k].qty > 0 && !(P && k === P.pid)) return true;
    return false;
  }

  /* One row per product with a discount, in cart order (own product first),
     from the server's allocations; then one per cart-level application; then
     anything the server discounted that neither accounts for, so the receipt
     always adds up to the server's total. */
  function discounts(G, c) {
    var out = [], sum = 0, order = [];
    if (P && P.pid && G.byP[P.pid]) order.push(P.pid);
    G.seq.forEach(function (vid) { var pid = G.byV[vid].pid; if (order.indexOf(pid) < 0) order.push(pid); });
    order.forEach(function (pid) {
      var p = G.byP[pid];
      if (!p || p.disc <= 0) return;
      out.push({ key: 'p' + pid, gift: p.free > 0, cents: p.disc,
                 label: shortTitle(p.title) + ' · ' + (p.free ? p.free + ' free' : (lc1(p.titles[0]) || 'discount')) });
      sum += p.disc;
    });
    each(c && c.cart_level_discount_applications, function (a, i) {
      var amt = a.total_allocated_amount || 0;
      if (amt <= 0) return;
      out.push({ key: 'c' + i + (a.title || ''), gift: false, cents: amt, label: a.title || 'Discount' });
      sum += amt;
    });
    var rest = ((c && c.total_discount) || 0) - sum;
    if (rest > 0) out.push({ key: 'rest', gift: false, cents: rest, label: 'Discount' });
    return out;
  }

  /* The progress line: the page's own product only (never item_count), named
     when anything else is in the cart. A claim ("pair price on", "free ...
     yours") is made only if the server shows that product's discount, or while
     a tap is still optimistic. Otherwise the plain arithmetic, and a warning. */
  function progress(q, opt, others, sDisc, sFree) {
    if (!P || P.rule.type === 'none') return null;
    var R = P.rule, lbl, confirmed = opt || sDisc > 0 || sFree > 0;
    if (R.type === 'pair') {
      var pu = money(R.pairUnit);
      if (q >= 2 && !confirmed) console.warn('[mossante] cart: ' + q + ' in the cart but the server shows no pair discount');
      lbl = q === 0 ? 'two are ' + pu + ' each'
          : q === 1 ? '1 more — both drop to ' + pu
          : confirmed ? 'pair price on · ' + pu + ' each'
          : q + ' × ' + money(R.unit);
    } else {
      var cy = R.cycle || 3, r = q % cy;
      if (q >= cy && !confirmed) console.warn('[mossante] cart: ' + q + ' in the cart but the server shows nothing free');
      lbl = q === 0 ? 'every 3rd is free'
          : q >= cy && !confirmed ? q + ' × ' + money(R.unit)
          : r === 0 ? 'free ' + P.noun + ' yours'
          : r === cy - 1 ? '1 more — it’s free'
          : (cy - r) + ' more to a free one';
    }
    var name = shortTitle(P.title);
    return { type: R.type, q: q, confirmed: confirmed, label: others && name ? name + ': ' + lbl : lbl };
  }

  /* ---------- the drawer ---------- */
  var drw, scrim, body, foot, counts = [], totals = [];
  var P = null, last = null, booted = false, lastDiscs = [], dirtyOther = false;

  function isOpen() { return !!drw && drw.classList.contains('open'); }
  function rows() { return body ? body.querySelectorAll('[data-row]') : []; }
  function rowOf(vid) {
    var hit = null;
    each(rows(), function (r) { if (!hit && r.getAttribute('data-row') === String(vid)) hit = r; });
    return hit;
  }
  function isOwn(row) { return row.getAttribute('data-own') === '1'; }
  function qtyOf(row) { var n = row.querySelector('.n'); return n ? (parseInt(n.textContent, 10) || 0) : 0; }
  function unitOf(row) { return parseInt(row.getAttribute('data-unit'), 10) || 0; }
  function nodeOf(html) {
    var t = document.createElement('div');
    t.innerHTML = html;
    return t.firstChild;
  }

  function ownHint() {
    var R = P.rule;
    return R.type === 'pair' ? 'Tap <b>+</b> to add one. Two are ' + money(R.pairUnit) + ' each.'
         : R.type === 'b2g1' ? 'Tap <b>+</b> next to a ' + esc(P.word) + '. Every third ' + esc(P.noun) + ' is free.'
         : 'Tap <b>+</b> to add one.';
  }

  /* A two-part option ("Princess, gold plated") names the cut on the row and
     the finish on the small line under it, the way another product's variant
     is shown. On a phone the name cell is ~87px and nowrap, so the full name
     ran under the stepper. One-word colours are unchanged (2026-09-30). */
  function ownName(nm) {
    nm = String(nm);
    var i = nm.indexOf(',');
    return i < 0 ? nm : nm.slice(0, i) + '<small class="cpre">' + nm.slice(i + 1).replace(/^\s+/, '') + '</small>';
  }
  /* The page's own rows: unchanged markup - pre-order note, sold-out rows and
     zero rows stay exactly as the CSS knows them. Built once. */
  function ownRowHTML(co) {
    var oos = co.available === false;
    return '<div class="crow z" data-row="' + co.variant + '" data-own="1" data-unit="' + (P.rule.unit || 0) + '"' +
        (oos ? ' data-oos="1"' : '') + '>' +
      '<span class="cth"><img alt="" src="' + thumb(co.thumb || co.image) + '" loading="lazy" decoding="async"></span>' +
      '<span class="cnm">' + ownName(co.name) + (co.preorder ? '<small class="cpre">Ships a bit later · waiting on restock' +
        (P.rdate ? ' (' + esc(P.rdate) + ')' : '') + '</small>' : '') + '</span>' +
      '<span class="stp">' +
        '<button type="button" aria-label="One fewer ' + co.name + '">&minus;</button>' +
        '<span class="n nm">0</span>' +
        '<button type="button" aria-label="One more ' + co.name + '"' + (oos ? ' disabled' : '') + '>+</button>' +
      '</span>' +
      '<span class="cpr nm">' + (oos ? 'Sold out' : '') + '</span>' +
    '</div>';
  }

  /* Another product's variant: product name (linked) over the variant name,
     the same stepper, the row price. */
  function otherRowHTML(v) {
    var vt = v.vtitle && v.vtitle !== 'Default Title' ? v.vtitle : '';
    var nm = esc(shortTitle(v.title)), full = nm + (vt ? ' ' + esc(vt) : '');
    return '<div class="crow z" data-row="' + esc(v.vid) + '" data-pid="' + esc(v.pid) + '" data-unit="' + (v.unit || 0) + '">' +
      '<span class="cth">' + (v.img ? '<img alt="" src="' + esc(thumb(v.img)) + '" loading="lazy" decoding="async">' : '') + '</span>' +
      '<span class="cnm">' + (v.url ? '<a href="' + esc(v.url) + '">' + nm + '</a>' : nm) +
        (vt ? '<small class="cpre">' + esc(vt) + '</small>' : '') + '</span>' +
      '<span class="stp">' +
        '<button type="button" aria-label="One fewer ' + full + '">&minus;</button>' +
        '<span class="n nm">0</span>' +
        '<button type="button" aria-label="One more ' + full + '">+</button>' +
      '</span>' +
      '<span class="cpr nm"></span>' +
    '</div>';
  }

  function bindRow(row) {
    var bs = row.querySelectorAll('.stp button');
    if (bs[0]) bs[0].onclick = function () { step(row, -1); };
    if (bs[1]) bs[1].onclick = function () { step(row, 1); };
  }

  /* Optimistic: the number changes under the finger, the request follows. A
     second fast tap reads the pending value, so ++ really is +2. What is sent
     is the DELTA: the row can be stale, the server cart the job reads is not. */
  function step(row, d) {
    if (d > 0 && row.getAttribute('data-oos') === '1') return;
    var el = row.querySelector('.n');
    var was = parseInt(el.textContent, 10) || 0, q = Math.max(0, was + d);
    if (q === was) return;
    el.textContent = q;
    row.classList.toggle('z', q === 0);
    if (!isOwn(row)) dirtyOther = true;
    optimistic();
    change(row.getAttribute('data-row'), q, q - was);
  }

  function buildBody() {
    body.innerHTML = P
      ? '<p class="crowHint" data-hint-empty>' + ownHint() + '</p>' + P.colours.map(ownRowHTML).join('')
      : '<p class="crowHint" data-hint-empty>Your cart is empty.</p>';
    each(rows(), bindRow);
  }

  /* Append-only. A variant with no row gets one: after the last row of the
     same product, or at the end under "Also in your cart". Then every row's
     number, price and zero state is set from the server (0 when absent). */
  function sync(G) {
    if (!body.querySelector('[data-hint-empty]')) buildBody();
    G.seq.forEach(function (vid) {
      if (rowOf(vid)) return;
      var v = G.byV[vid], el = nodeOf(otherRowHTML(v)), anchor = null;
      each(rows(), function (r) { if (!isOwn(r) && r.getAttribute('data-pid') === v.pid) anchor = r; });
      if (anchor) anchor.parentNode.insertBefore(el, anchor.nextSibling);
      else {
        if (P && !body.querySelector('.crowGrp')) body.appendChild(nodeOf('<p class="crowGrp">Also in your cart</p>'));
        body.appendChild(el);
      }
      bindRow(el);
    });
    var ownQ = 0, n = 0;
    each(rows(), function (row) {
      var v = G.byV[row.getAttribute('data-row')], q = v ? v.qty : 0;
      var el = row.querySelector('.n');
      if (el && String(q) !== el.textContent) el.textContent = q;
      row.classList.toggle('z', q === 0);
      if (v && v.unit) row.setAttribute('data-unit', v.unit);
      var pr = row.querySelector('.cpr');
      if (pr) pr.textContent = q > 0 ? money(v.orig) : (row.getAttribute('data-oos') === '1' ? 'Sold out' : '');
      if (isOwn(row)) ownQ += q;
      n += q;
    });
    paintHint(ownQ, n);
  }

  function paintHint(ownQ, n) {
    var hint = body.querySelector('[data-hint-empty]');
    if (hint) hint.hidden = P ? ownQ !== 0 : n !== 0;
  }

  /* On close: drop other products' rows that reached 0, and the label when
     nothing is left under it. Never while open. */
  function prune() {
    if (!body) return;
    each(rows(), function (row) {
      if (!isOwn(row) && qtyOf(row) === 0) row.parentNode.removeChild(row);
    });
    var any = false;
    each(rows(), function (row) { if (!isOwn(row)) any = true; });
    var grp = body.querySelector('.crowGrp');
    if (grp && !any) grp.parentNode.removeChild(grp);
  }

  function paintBadges(n) {
    each(counts, function (e) { e.textContent = n; });
    each(document.querySelectorAll('[data-bag]'), function (e) { e.classList.toggle('has', n > 0); });
    each(document.querySelectorAll('.hdBag'), function (e) { e.classList.toggle('has', n > 0); });
  }

  /* ---------- the footer: built once, then text and classes only ---------- */
  function buildFoot() {
    var help = foot.querySelector('[data-drw-help]');
    var game = '';
    if (P && P.rule.type === 'b2g1') {
      /* the game, drawn: two slots and a gift box per cycle */
      game = '<div class="game">' +
          '<span class="gWon" data-gwon hidden></span>' +
          '<span class="gs" data-s1></span>' +
          '<span class="gs" data-s2></span>' +
          '<span class="gs gGift" data-s3>' + GIFT + '</span>' +
          '<span class="gLbl" data-glbl aria-live="polite"></span>' +
        '</div>';
    } else if (P && P.rule.type === 'pair') {
      /* two slots: one filled at one piece, both at two - the pair price */
      game = '<div class="game">' +
          '<span class="gs" data-s1></span>' +
          '<span class="gs" data-s2></span>' +
          '<span class="gLbl" data-glbl aria-live="polite"></span>' +
        '</div>';
    }
    foot.innerHTML = game +
      /* the receipt: subtotal, one discount row per product, the total */
      '<div class="mrow" data-rsub hidden><span>Subtotal</span><span class="nm" data-vsub></span></div>' +
      '<div data-discs></div>' +
      '<div class="sumRow">' +
        '<span class="sumK">Total<small data-ssub>Free shipping</small></span>' +
        '<span class="sumV nm" data-sval>$0</span>' +
      '</div>' +
      /* why the last change did not take; empty and hidden otherwise */
      '<p class="drwErr" data-cart-err role="status" hidden></p>' +
      '<button class="btn gd" disabled data-checkout>Cart is empty</button>';
    if (help) foot.appendChild(help);
    foot.querySelector('[data-checkout]').onclick = checkout;
  }

  /* Checkout. With writes still queued, going now would kill every one not yet
     sent and check out a smaller cart than the drawer shows (review R2). So
     wait for the queue; go only if every write took and the cart is not empty.
     Otherwise stay: the drawer now shows the server's cart and the reason. */
  var leaving = false;
  function checkout() {
    var b = this;
    if (b.disabled) return;
    if (inflight === 0) { window.location.href = '/checkout'; return; }
    var f0 = fails;
    leaving = true; b.disabled = true; setT(b, 'Updating…');
    idle().then(function () {
      leaving = false;
      var n = last ? last.item_count : 0;
      if (!last) each(rows(), function (r) { n += qtyOf(r); });
      if (fails === f0 && n > 0) { window.location.href = '/checkout'; return; }
      b.disabled = n === 0; setT(b, n === 0 ? 'Cart is empty' : 'Checkout');
    });
  }
  var ERR = 'That change did not go through. Check your connection and try again.';
  function paintErr(msg) {
    var el = foot && foot.querySelector('[data-cart-err]');
    if (!el) return;
    el.hidden = !msg;
    setT(el, msg || '');
  }

  /* re-trigger a CSS animation on an element that may already carry it */
  function pop(el, cls) {
    if (!el) return;
    el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls);
  }
  function setT(el, t) { if (el && el._t !== t) { el._t = t; el.textContent = t; return true; } return false; }

  var lastQ = -1, lastN = -1;
  function paintGame(g) {
    if (!g) return;
    var s1 = foot.querySelector('[data-s1]'), s2 = foot.querySelector('[data-s2]'), s3 = foot.querySelector('[data-s3]');
    var q = g.q, grew = lastQ >= 0 && q > lastQ;
    lastQ = q;
    if (g.type === 'pair') {
      var pair = q >= 2;
      if (s1) s1.classList.toggle('on', q >= 1);
      if (s2) { s2.classList.toggle('on', pair); s2.classList.toggle('won', pair && g.confirmed); }
      if (grew) { if (pair && q === 2) pop(s2, 'popBig'); else pop(q === 1 ? s1 : s2, 'pop'); }
    } else {
      /* slots for the CURRENT cycle; at a multiple of three the cycle shows
         complete and the box won - only if the server shows it free */
      var r = q % 3, free = Math.floor(q / 3), mult = q > 0 && r === 0, full = mult && g.confirmed;
      if (s1) s1.classList.toggle('on', mult || r >= 1);
      if (s2) s2.classList.toggle('on', mult || r >= 2);
      if (s3) s3.classList.toggle('won', full);
      if (grew) { if (full) pop(s3, 'popBig'); else pop(r === 1 ? s1 : s2, 'pop'); }
      var won = foot.querySelector('[data-gwon]');
      var earlier = !g.confirmed ? 0 : full ? free - 1 : free;
      if (won) {
        won.hidden = earlier <= 0;
        var wH = '';
        for (var w = 0; w < Math.min(earlier, 3); w++) wH += '<span class="gs gGift won small">' + GIFT + '</span>';
        if (earlier > 3) wH += '<b class="nm">+' + (earlier - 3) + '</b>';
        if (won._h !== wH) { won._h = wH; won.innerHTML = wH; }
      }
    }
    setT(foot.querySelector('[data-glbl]'), g.label);
  }

  /* Discount rows are a keyed list (data-dk), updated in place, never rebuilt:
     a row that is torn down and remade on every response is flicker. */
  function paintDiscs(discs, grew) {
    var box = foot.querySelector('[data-discs]');
    if (!box) return;
    var keep = {};
    discs.forEach(function (d, i) {
      keep[d.key] = 1;
      var el = null;
      each(box.children, function (c) { if (!el && c.getAttribute('data-dk') === d.key) el = c; });
      if (!el) {
        el = nodeOf('<div class="mrow disc" data-dk="' + esc(d.key) + '">' +
          '<span><span class="dGift" hidden>' + GIFT + '</span><span data-dlbl></span></span>' +
          '<span class="nm" data-vdisc></span></div>');
        box.insertBefore(el, box.children[i] || null);
      }
      var g = el.querySelector('.dGift');
      if (g) g.hidden = !d.gift;
      setT(el.querySelector('[data-dlbl]'), d.label);
      if (setT(el.querySelector('[data-vdisc]'), '−' + money(d.cents)) && grew) pop(el, 'pop');
    });
    each([].slice.call(box.children), function (c) {
      if (!keep[c.getAttribute('data-dk')]) box.removeChild(c);
    });
  }

  /* One painter for the optimistic tap and the server response, so the two
     can never disagree about layout - only, briefly, about numbers.
     S = {n, sub, total, discs, prog, pending} */
  function paintFoot(S) {
    if (!foot.querySelector('[data-checkout]')) buildFoot();
    var grew = lastN >= 0 && S.n > lastN;
    lastN = S.n;
    paintGame(S.prog);
    var saved = 0;
    S.discs.forEach(function (d) { saved += d.cents; });
    paintDiscs(S.discs, grew);
    var rsub = foot.querySelector('[data-rsub]');
    if (rsub) rsub.hidden = saved <= 0;
    setT(foot.querySelector('[data-vsub]'), money(S.sub));
    setT(foot.querySelector('[data-ssub]'), 'Free shipping' + (saved > 0 ? ' · you save ' + money(saved) : ''));
    setT(foot.querySelector('[data-sval]'), money(S.total));
    if (S.pending) foot.setAttribute('data-pending', '');
    else foot.removeAttribute('data-pending');
    var co = foot.querySelector('[data-checkout]');
    if (co && leaving) { co.disabled = true; setT(co, 'Updating…'); }
    else if (co) { co.disabled = S.n === 0; setT(co, S.n === 0 ? 'Cart is empty' : 'Checkout'); }
  }

  /* A tap: paint its whole consequence now. n and the subtotal sum ALL rows.
     The page's own discount follows its rule; other products keep the
     server's last word and the total is dimmed until the queue drains. */
  function optimistic() {
    var n = 0, sub = 0, ownQ = 0, others = false, unknown = false;
    each(rows(), function (row) {
      var q = qtyOf(row), unit = unitOf(row);
      n += q; sub += q * unit;
      if (q > 0 && !unit) unknown = true;
      var pr = row.querySelector('.cpr');
      if (pr) pr.textContent = q > 0 ? (unit ? money(q * unit) : pr.textContent) : (row.getAttribute('data-oos') === '1' ? 'Sold out' : '');
      if (isOwn(row)) ownQ += q; else if (q > 0) others = true;
    });
    paintBadges(n);
    paintHint(ownQ, n);
    var discs = [], ownKey = P ? 'p' + P.pid : null, ownOld = null;
    lastDiscs.forEach(function (d) { if (d.key === ownKey) ownOld = d; else discs.push(d); });
    if (P) {
      var R = P.rule, od = 0, lbl = '';
      if (R.type === 'pair') {
        od = ownQ >= 2 ? ownQ * Math.max(0, R.unit - R.pairUnit) : 0;
        lbl = ownOld ? ownOld.label : shortTitle(P.title) + ' · pair price';
      } else if (R.type === 'b2g1') {
        var k = Math.floor(ownQ / (R.cycle || 3)) * (R.freeN || 1);
        od = k * R.unit;
        lbl = shortTitle(P.title) + ' · ' + k + ' free';
      }
      if (od > 0) discs.unshift({ key: ownKey, gift: R.type === 'b2g1', cents: od, label: lbl });
    }
    var saved = 0;
    discs.forEach(function (d) { saved += d.cents; });
    paintFoot({ n: n, sub: sub, total: sub - saved, discs: discs,
                prog: progress(ownQ, true, others, 0, 0), pending: dirtyOther || unknown });
  }

  function render(c) {
    if (!c) return;
    last = c; good = c;         /* good: the last cart the server confirmed */
    if (inflight > 0) return;   /* optimistic paint stands until the queue drains */
    if (!body) return;
    var G = group(c);
    sync(G);
    if (!isOpen()) prune();
    paintBadges(c.item_count);
    each(totals, function (e) { e.textContent = money(c.total_price); });
    lastDiscs = discounts(G, c);
    var own = P && P.pid ? G.byP[P.pid] : null;
    paintFoot({
      n: c.item_count,
      sub: c.original_total_price != null ? c.original_total_price : c.total_price + (c.total_discount || 0),
      total: c.total_price,
      discs: lastDiscs,
      prog: progress(own ? own.qty : 0, false, othersIn(G), own ? own.disc : 0, own ? own.free : 0),
      pending: false
    });
  }

  /* ---------- the one queue ---------- */
  /* Every cart write goes through it. Two fast taps, a tap during an add, an
     add during a tap: responses rendering out of order is exactly the
     twitching this cart was rebuilt to kill. The job's own promise is
     returned, so the picker can show "Adding..." and its error. */
  var busy = Promise.resolve(), inflight = 0, epoch = 0;
  var good = null, fails = 0, errMsg = '';
  function pull() { return req('/cart.js').then(render); }
  function enqueue(fn) {
    if (inflight === 0) { errMsg = ''; paintErr(''); }   /* a new burst: the old reason goes */
    inflight++; epoch++;
    var job = busy.then(fn);
    busy = job.then(noop, function (e) {
        fails++;
        errMsg = (e && e.description) || ERR;
        last = null;
        return pull().catch(noop);
      })
      .then(function () {
        /* only when the queue drains does the server get the brush back */
        inflight--;
        if (inflight > 0) return;
        dirtyOther = false;
        var note = errMsg; errMsg = '';
        if (last) { render(last); paintErr(note); return; }
        /* no server word since the last write: ask once more; if that fails
           too, show the last cart the server confirmed - never leave an unsaved
           tap on screen as if it were the cart (review R5) */
        pull().then(function () { if (inflight === 0) paintErr(note); },
          function () { if (inflight === 0) { if (good) render(good); paintErr(note || ERR); } });
      })
      /* a throw while painting must not jam the queue for every later write */
      .catch(function (e) { if (window.console) console.error('[mossante] cart:', e); });
    return job;
  }
  /* Resolves once the queue is empty, including writes queued while waiting. */
  function idle() {
    var b = busy;
    return b.then(function () { return b === busy ? null : idle(); });
  }

  /* Keyed by VARIANT for the shopper, written by LINE KEY for Shopify: the new
     total on the variant's first line, its other lines to 0. Keys come from
     the latest cart, which the serialized queue guarantees is the one the
     previous write returned. Not in the cart yet: by variant id adds it. */
  function keyedUpdates(c, variantId, q) {
    var lines = ((c && c.items) || []).filter(function (it) { return String(it.variant_id) === String(variantId); });
    var u = {};
    if (!lines.length) { u[variantId] = q; return u; }
    lines.forEach(function (it, i) { u[it.key] = i === 0 ? q : 0; });
    return u;
  }
  function qtyIn(c, vid) {
    var n = 0;
    each(c && c.items, function (it) { if (String(it.variant_id) === String(vid)) n += it.quantity; });
    return n;
  }
  /* A tap: delta is +1 / -1 (the stepper). Without a delta, q is an absolute
     quantity (the public MossanteCart.change(variantId, q)). */
  function change(variantId, q, delta) {
    /* the first write since the queue was idle may follow a stale screen (bfcache,
       another tab, a read that never came back): it starts from a fresh read */
    var burst = inflight === 0;
    return enqueue(function () {
      var asked = null;
      var target = function (c) {
        return delta == null ? Math.max(0, q) : Math.max(0, qtyIn(c, variantId) + delta);
      };
      var write = function (c) {
        asked = target(c);
        if (asked === qtyIn(c, variantId)) return Promise.resolve(c);   /* nothing to change */
        return req('/cart/update.js', { updates: keyedUpdates(c, variantId, asked) });
      };
      var start = last && !burst ? Promise.resolve(last) : req('/cart.js');
      return start.then(write)
        .catch(function (e) {
          /* Refused (4xx - a stale line key after another tab changed the cart):
             nothing was applied, so apply the tap to the fresh cart. Lost (no
             answer, or a 5xx): it may have landed - if the fresh cart already
             holds what was asked, it did, and a second write would count the
             tap twice. */
          var lost = asked != null && !(e && e.http >= 400 && e.http < 500);
          return req('/cart.js').then(function (c) {
            if (lost && qtyIn(c, variantId) === asked) return c;
            return write(c);
          });
        })
        .then(render);
    }).catch(noop);
  }
  /* The picker's Add. Inside the queue, so it can never race a stepper, and
     shown in the rows at once. Its promise is the Add alone: if only the read
     after it fails, the items ARE in the cart, and telling the shopper "could
     not add" made her add them twice (review R3). The drain reads again. */
  function add(items) {
    boot();
    var job = enqueue(function () {
      var before = good;   /* the last cart the server confirmed, before this Add */
      return req('/cart/add.js', { items: items })
        .then(function () { return req('/cart.js').then(render, function () { last = null; }); },
          function (e) {
            if (e && e.http >= 400 && e.http < 500) throw e;   /* refused: nothing was added */
            /* lost on the way back: if the cart now holds exactly what the Add
               asked for on top of the cart before it, the Add landed - saying
               "could not add" would make her add it twice */
            return req('/cart.js').then(function (c) {
              var landed = !!before && items.every(function (it) {
                return qtyIn(c, it.id) === qtyIn(before, it.id) + (parseInt(it.quantity, 10) || 0);
              });
              if (!landed) throw e;
              render(c);
            }, function () { throw e; });
          });
    });
    showAdd(items);
    return job;
  }
  /* The Add, painted before the server answers: the drawer opens over the
     picker, and rows still reading 0 invited a + that undid the Add (R1). */
  function showAdd(items) {
    if (!body || !foot) return;
    if (!body.querySelector('[data-hint-empty]')) buildBody();
    var first = null;
    each(items, function (it) {
      var row = rowOf(it.id), k = parseInt(it.quantity, 10) || 0;
      if (!row || k <= 0) return;
      if (!first) first = row;
      var q = qtyOf(row) + k;
      row.querySelector('.n').textContent = q;
      row.classList.toggle('z', q === 0);
    });
    dirtyOther = true;    /* not confirmed yet: the total stays dimmed until the drain */
    optimistic();
    reveal(first);
  }
  /* A product with more options than the popup shows (the Solitaire: twelve
     fixed rows, 2026-09-30) would add a row below the fold, and the shopper
     sees twelve zeros. Scroll the list, never the page, so the first row the
     Add changed is in view. Nothing moves when that row is already visible,
     which is every Add on a tall phone for the cord's six rows. */
  function reveal(row) {
    if (!row || !body || !(body.scrollHeight > body.clientHeight)) return;
    var top = row.offsetTop - body.offsetTop, bot = top + row.offsetHeight;
    if (!(top >= 0)) return;
    if (top < body.scrollTop || bot > body.scrollTop + body.clientHeight) body.scrollTop = Math.max(0, top - 8);
  }
  /* A read outside the queue. While a write is queued the drain paints, and a
     read that a write overtook is dropped - it may predate that write. */
  function refresh() {
    boot();
    if (inflight > 0) return busy;
    var e = epoch;
    return req('/cart.js').then(function (c) {
      if (e === epoch && inflight === 0) render(c);
      return c;
    });
  }

  /* ---------- open, close, scroll lock ---------- */
  /* The page must not scroll behind the popup. overflow:hidden alone does not
     hold on iOS Safari, so pin the body and give the scroll position back. */
  var lockY = 0;
  function lockBody() {
    lockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var b = document.body;
    b.style.position = 'fixed'; b.style.top = (-lockY) + 'px';
    b.style.left = '0'; b.style.right = '0'; b.style.width = '100%';
  }
  function unlockBody() {
    var b = document.body;
    b.style.position = ''; b.style.top = ''; b.style.left = ''; b.style.right = ''; b.style.width = '';
    window.scrollTo(0, lockY);
  }
  function open() {
    boot();
    if (!drw) return;
    /* opening twice must not re-read a locked scroll position of 0 - that is
       how close() would throw the shopper back to the page top */
    if (isOpen()) { refresh(); return; }
    each(document.querySelectorAll('.flyer'), function (g) { if (g.parentNode) g.parentNode.removeChild(g); });
    drw.classList.add('open'); if (scrim) scrim.classList.add('open'); lockBody(); refresh();
  }
  function close() {
    if (!isOpen()) return;
    drw.classList.remove('open'); if (scrim) scrim.classList.remove('open'); unlockBody();
    prune();
    if (inflight === 0) paintErr('');
  }

  function boot() {
    if (booted) return;
    drw = document.querySelector('[data-drw]');
    if (!drw) return;
    booted = true;
    scrim = document.querySelector('[data-scrim]');
    body = drw.querySelector('[data-drwb]');
    foot = drw.querySelector('[data-drwf]');
    counts = document.querySelectorAll('[data-cart-count]');
    totals = document.querySelectorAll('[data-cart-total]');
    P = readPage();
    /* Delegated: the picker renders its own buttons after this runs, and a
       direct binding would leave those dead. */
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('[data-open-cart]') : null;
      if (t) { e.preventDefault(); open(); }
    });
    var x = drw.querySelector('[data-close]'); if (x) x.onclick = close;
    if (scrim) scrim.onclick = close;
    window.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    /* A page restored by Back (bfcache) or a tab coming back to the front shows
       whatever it showed when it was left - often an open drawer. The drawer's
       product links make that path common: open Marquise, add one, press Back.
       Read the cart again, and drop a Checkout that was waiting (review R4). */
    window.addEventListener('pageshow', function (e) { if (e && e.persisted) { leaving = false; refresh(); } });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'hidden') refresh();
    });
    refresh();
  }

  window.MossanteCart = { open: open, close: close, refresh: refresh, add: add, change: change, _shared: 1 };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
